import { z } from 'zod'
import { LANGUAGES } from './simulation'

/** 인앱 알림 종류. 푸시와 같은 내용을 알림 탭에도 남긴다 (2026-09-16: 최대한 많이 보내는 방향) */
export const NOTIFICATION_TYPES = [
  'sim_done',
  'quote_submitted',
  'quote_quoted',
  'quote_closed',
  'post_comment',
  'comment_reply',
  'post_vote',
  'plan_changed',
  'plan_limit',
  'quota_warning',
  'welcome',
  /** 생성 실패 (1.0.1). 1.0.0 앱은 모르는 종류라 기본 아이콘으로 그린다 */
  'sim_failed',
  /**
   * 재방문 알림 (2026-10-29, API lib/revisit-notifications.ts). 매시 크론이 보내는 광고성 알림이라 수신 동의한 회원에게만 간다.
   * 모르는 옛 앱(1.0.3~)은 기본(welcome) 아이콘으로 그리고, 눌러서 가는 화면(페이월·견적 요청)은 옛 앱에도 있다
   */
  'plan_limit_reminder',
  'quote_nudge',
  /** 전문가 검수 완료·반려 (2026-10-29, 로드맵 매출 8위). 옛 앱은 기본 아이콘으로 그린다 */
  'expert_review',
  /**
   * 생성 횟수 초기화 3일 전 안내 (2026-10-30, API lib/generation-reset-reminder.ts). 남은 생성이 이월되지 않고 초기화된다는
   * 이용 안내. 모르는 옛 앱은 기본 아이콘으로 그리고, 눌러서 가는 홈은 옛 앱에도 있다
   */
  'generation_reset_reminder',
  /**
   * 레시피캣 중계 샘플 주문의 결제 확인·공장 진행 (AI 중계 샘플, 2026-10-30). 거래성. 이 종류를 받는 사람은 샘플 화면이 있는
   * 새 앱에서 주문한 사람뿐이다
   */
  'sample_update',
  /** 공장에서 메시지·역제안이 왔다 (AI 중계 메시지). 거래성. 내용은 싣지 않는다 */
  'sample_message',
] as const
export type NotificationType = (typeof NOTIFICATION_TYPES)[number]

/**
 * 알림·푸시의 url 이 앱 내부 화면 경로(expo-router href)인지.
 * (2026-10-08) '/' 로 시작하고 '//'·역슬래시·공백이 없어야 한다. 'https://…', 'mailto:', '//evil.com' 같은
 * 외부 주소를 알림으로 열게 하지 않는다. 앱(push.ts·알림 탭)과 관리자 전송 스키마가 같이 쓴다.
 */
export function isInAppPath(url: unknown): url is string {
  return typeof url === 'string' && url.length <= 200 && /^\/(?![/\\])[^\\\s]*$/.test(url)
}

/** 관리자가 보내는 알림의 이동 경로 — 앱 내부 경로만 */
export const inAppPathSchema = z
  .string()
  .max(200)
  .refine(isInAppPath, { message: "App path must start with '/' (no external URLs)" })

export interface NotificationView {
  id: string
  type: NotificationType
  title: string
  body: string
  /** 탭하면 갈 화면 (expo-router href) */
  url: string
  readAt: string | null
  createdAt: string
}

export const notificationListQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(50).default(30),
  cursor: z.string().datetime().optional(),
  /** 앱 언어 — 저장된 알림 문구를 이 언어로 다시 렌더링한다 */
  lang: z.enum(LANGUAGES).optional(),
})

/** POST /me/notifications/read — ids 를 주면 그것만, all 이면 전부 */
export const markReadSchema = z.object({
  ids: z.array(z.string().min(1)).max(100).optional(),
  all: z.boolean().optional(),
})
